import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress
} from "@mui/material";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTabBar } from "@maersk-global/mds-react-wrapper/components-core/mc-tab-bar";
import { McTab } from "@maersk-global/mds-react-wrapper/components-core/mc-tab";
import { TableContext } from "../context";
import { toast } from 'react-toastify';
import { setGlobalContextValues } from "../axiosConfig";
// Import new components
import OverviewCards from "./AppOnboarding/OverviewCards";
import ApplicationTable from "./AppOnboarding/ApplicationTable";
import FilterSection from "./AppOnboarding/FilterSection";
import OnboardingWizard from "./AppOnboarding/OnboardingWizard";
import OnboardingRequestsTable from "./AppOnboarding/OnboardingRequestsTable";

// Function to map API data to component format
const mapApiDataToComponentFormat = (apiData) => {
  // Helper function to get display name for onboarding status
  const getOnboardingStatusDisplay = (status) => {
    switch (status) {
      case 'onboarded': return 'Onboarded';
      case 'in_progress': return 'In Progress';
      case 'pending': return 'Pending';
      default: return status;
    }
  };


  // Helper function to get process type display
  const getProcessTypeDisplay = (changeType, channel) => {
    if (changeType === 'rapid') {
      return channel === 'green' ? 'Rapid (Green)' : 'Rapid (Red)';
    } else if (changeType === 'classic') {
      return 'Classic';
    } else if (changeType === 'classic+rapid') {
      return channel === 'green' ? 'Classic+Rapid (Green)' : 'Classic+Rapid (Red)';
    }
    return changeType;
  };

  // Transform application data from new format
  const transformApplication = (app, processType) => ({
    id: `${processType}-${app.application_name.replace(/\s+/g, '-')}`,
    name: app.application_name,
    code: app.application_id || 'N/A',
    owner: Array.isArray(app.application_owner) ? app.application_owner.join(', ') : app.application_owner,
    onboardingStatus: getOnboardingStatusDisplay(app.onboarding_status),
    processType: getProcessTypeDisplay(app.change_process_type, app.deployment_channel),
    lastChange: app.last_change_date,
    changesLast3Months: typeof app.changes_raised === 'number' ? app.changes_raised : 0,
    channelStatus: app.deployment_channel,
    deploymentChannel: app.deployment_channel,
    visibilityHealth: app.operational_health === 'recent_changes',
    changeProcessType: processType, 
    mvc: app.mvc || 'No', 
    bca: app.bca || 'No', 
    isMvc: (app.mvc || 'No').toLowerCase() === 'yes', 
    isBca: (app.bca || 'No').toLowerCase() === 'yes'  
  });

  // Helper function to filter out applications with empty or missing application_id
  const hasValidApplicationId = (app) => {
    return app.application_id && app.application_id.trim() !== '';
  };

  // Process the new data structure with three process types, filtering out apps with empty IDs
  const classicChangeApps = (apiData.data?.classic_change || [])
    .filter(hasValidApplicationId)
    .map(app => transformApplication(app, 'classic'));
  const rapidChangeApps = (apiData.data?.rapid_change || [])
    .filter(hasValidApplicationId)
    .map(app => transformApplication(app, 'rapid'));
  const classicRapidChangeApps = (apiData.data?.['classic_change+rapid_change'] || [])
    .filter(hasValidApplicationId)
    .map(app => transformApplication(app, 'classic+rapid'));
  
  // Process not onboarded applications with new metadata structure, filtering out apps with empty IDs
  const notOnboardedApps = (apiData.data?.not_onboarded || [])
    .filter(hasValidApplicationId)
    .map(appData => ({
      name: appData.application_name,
      code: appData.application_id || 'N/A',
      owners: Array.isArray(appData.owners) ? appData.owners : [appData.owners || 'N/A'],
      mvc: appData.mvc || 'No', 
      bca: appData.bca || 'No', 
      isMvc: (appData.mvc || 'No').toLowerCase() === 'yes', 
      isBca: (appData.bca || 'No').toLowerCase() === 'yes'  
    }));

  // Unique app IDs from all sources (including classic+rapid apps)
  const allOnboardedAppIds = [
    ...classicChangeApps.map(a => a.code),
    ...rapidChangeApps.map(a => a.code),
    ...classicRapidChangeApps.map(a => a.code),
  ];
  const uniqueOnboardedAppIds = Array.from(new Set(allOnboardedAppIds));

  // Create overview cards data structure
  const overviewCards = {
    allApplications: {
      notOnboardedApps,
      uniqueOnboardedAppIds
    },
    visibilityHealth: {
      changesVisible: [],
      changesNotVisible: []
    },
    deploymentChannels: {
      greenChannelApps: [],
      redChannelApps: []
    }
  };

  // Process visibility health
  const allOnboardedAppIds_Set = new Set();
  
  // Check classic change apps for visibility health
  (apiData.data?.classic_change || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      allOnboardedAppIds_Set.add(appId);
      if (app.operational_health === "recent_changes") {
        if (!overviewCards.visibilityHealth.changesVisible.includes(appId)) {
          overviewCards.visibilityHealth.changesVisible.push(appId);
        }
      }
    });

  // Check rapid change apps for visibility health
  (apiData.data?.rapid_change || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      allOnboardedAppIds_Set.add(appId);
      if (app.operational_health === "recent_changes") {
        if (!overviewCards.visibilityHealth.changesVisible.includes(appId)) {
          overviewCards.visibilityHealth.changesVisible.push(appId);
        }
      }
    });

  // Check classic+rapid change apps for visibility health
  (apiData.data?.['classic_change+rapid_change'] || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      allOnboardedAppIds_Set.add(appId);
      if (app.operational_health === "recent_changes") {
        if (!overviewCards.visibilityHealth.changesVisible.includes(appId)) {
          overviewCards.visibilityHealth.changesVisible.push(appId);
        }
      }
    });

  // Add apps that don't have recent changes to changesNotVisible
  allOnboardedAppIds_Set.forEach(appId => {
    if (!overviewCards.visibilityHealth.changesVisible.includes(appId)) {
      overviewCards.visibilityHealth.changesNotVisible.push(appId);
    }
  });

  // Add not onboarded apps to changesNotVisible
  notOnboardedApps.forEach(app => {
    if (!overviewCards.visibilityHealth.changesNotVisible.includes(app.code)) {
      overviewCards.visibilityHealth.changesNotVisible.push(app.code);
    }
  });

  // Process deployment channels (only for onboarded apps)
  // Check classic change apps for deployment channels
  (apiData.data?.classic_change || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      if (app.deployment_channel === "red") {
        if (!overviewCards.deploymentChannels.redChannelApps.includes(appId)) {
          overviewCards.deploymentChannels.redChannelApps.push(appId);
        }
      }
    });

  // Check rapid change apps for deployment channels
  (apiData.data?.rapid_change || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      if (app.deployment_channel === "red") {
        if (!overviewCards.deploymentChannels.redChannelApps.includes(appId)) {
          overviewCards.deploymentChannels.redChannelApps.push(appId);
        }
      }
    });

  // Check classic+rapid change apps for deployment channels
  (apiData.data?.['classic_change+rapid_change'] || [])
    .filter(hasValidApplicationId)
    .forEach(app => {
      const appId = app.application_id || 'N/A';
      if (app.deployment_channel === "red") {
        if (!overviewCards.deploymentChannels.redChannelApps.includes(appId)) {
          overviewCards.deploymentChannels.redChannelApps.push(appId);
        }
      }
    });

  // Add apps that don't have red channel to greenChannelApps (only onboarded apps)
  allOnboardedAppIds_Set.forEach(appId => {
    if (!overviewCards.deploymentChannels.redChannelApps.includes(appId)) {
      overviewCards.deploymentChannels.greenChannelApps.push(appId);
    }
  });

  return {
    classicChangeApps,
    rapidChangeApps,
    classicRapidChangeApps, // Include the new apps type
    overviewCards
  };
};


// Filter red channel apps from all apps
// const redChannelApps = [
//   ...myApplications.filter(app => app.isRedChannel),
//   ...platformApps.filter(app => app.isRedChannel),
//   ...portfolioApps.filter(app => app.isRedChannel)
// ];

/**
 * ApplicationOnboarding component manages the application onboarding dashboard.
 * Displays applications in a card-based layout with beacon indicators.
 * 
 * @component
 * @returns {JSX.Element} The rendered component.
 */
const ApplicationOnboarding = () => {
  // All available platforms list
  const allAvailablePlatforms = [
    'AIML Engineering Platform', 'Air & LCL Execution Platform', 'AI Services Platform', 
    'Asset Management Platform', 'Assets Strategy Platform', 'Brand Integration/UX Platform', 
    'Business Enabling (APMT)', 'Capacity & Container Management Platform', 
    'Cloud & Enterprise Service Engineering', 'Cold Chain Logistics Platform', 
    'Container & Workflow Automation', 'Corporate Affairs, Strategy & Transformation Platform', 
    'Customer Relationship Platform', 'Customs Integration Platform', 'Cyber Architecture', 
    'Cyber Governance', 'Cyber Operations', 'Cyber Risk', 'Database & Compute Engineering', 
    'Data Platform', 'Depots Platform', 'Energy Markets Platform', 'Energy Transition Platform', 
    'Enterprise Architecture', 'Finance & Tax Platform', 'Fleet & Asset Management Platform', 
    'Industrial Infrastructure Platform', 'Integrated Routing Platform', 
    'Integrated Supply Chain Engine (ISCE)', 'Integration Engineering', 'Integration Platform', 
    'Landside Execution Platform', 'L & S Automation and Operation Technology', 
    'Maersk.com Platform', 'Maersk Contract Logistics', 'Maersk E-Commerce Platform', 
    'Maersk Ground Freight Platform', 'Maestro Data Platform', 'Master Data Platform', 
    'Network Design Platform', 'Network Engineering', 'Network Execution and Port Planning Platform', 
    'Operations Technology (APMT)', 'Order Handling Platform', 'People Platform', 
    'Plan & Book Platform', 'Platform Cyber Engineering', 'Platform Engineering', 
    'Platform not defined', 'Project Logistics Platform', 'Reporting Platform', 
    'Research Operational Analytics & Data Platform(ROAD)', 'SAP Technology Platform', 
    'Scheduling and Berth Planning Platform', 'Supply Chain Platform', 'TbM Data & Analytics', 
    'Technology Operations (APMT)', 'Technology Operations Platform (Ocean & Logistics)'
  ];

  // All available portfolios list
  const allAvailablePortfolios = [
    'Technology Service Engineering', 'Cyber Security', 'Global Data & Analytics', 
    'APMT, IOT and Automation', 'Managed By Maersk', 'Fulfilled By Maersk', 
    'Transported By Maersk', 'Corporate Platforms', 'Business Enabling Platforms', 
    'Portfolio not defined'
  ];

  const [selectedTab, setSelectedTab] = useState(0);
  
  // Filter states for Applications tab
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [processTypeFilter, setProcessTypeFilter] = useState("All Processes");
  const [isMvcOn, setIsMvcOn] = useState(false);
  const [isBcaOn, setIsBcaOn] = useState(false);
  
  // Filter states for Platform tab
  const [platformSearchTerm, setPlatformSearchTerm] = useState("");
  const [platformStatusFilter, setPlatformStatusFilter] = useState("All Statuses");
  const [platformProcessTypeFilter, setPlatformProcessTypeFilter] = useState("All Processes");
  const [platformIsMvcOn, setPlatformIsMvcOn] = useState(false);
  const [platformIsBcaOn, setPlatformIsBcaOn] = useState(false);
  
  // Filter states for Portfolio tab
  const [portfolioSearchTerm, setPortfolioSearchTerm] = useState("");
  const [portfolioStatusFilter, setPortfolioStatusFilter] = useState("All Statuses");
  const [portfolioProcessTypeFilter, setPortfolioProcessTypeFilter] = useState("All Processes");
  const [portfolioIsMvcOn, setPortfolioIsMvcOn] = useState(false);
  const [portfolioIsBcaOn, setPortfolioIsBcaOn] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isOnboardingTokenFetched, setIsOnboardingTokenFetched] = useState(false);
  const [isCMDBTokenFetched, setIsCMDBTokenFetched] = useState(false);

  // Define the Maersk font family
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';
  
  // Data states for different tabs
  const [applicationsData, setApplicationsData] = useState({
    classicChangeApps: [],
    rapidChangeApps: [],
    classicRapidChangeApps: [],
    overviewCards: {
      allApplications: {
        notOnboardedApps: [],
        uniqueOnboardedAppIds: []
      },
      visibilityHealth: {
        changesVisible: [],
        changesNotVisible: []
      },
      deploymentChannels: {
        greenChannelApps: [],
        redChannelApps: []
      }
    }
  });
  
  // Platform and Portfolio states
  const [rawPlatformsData, setRawPlatformsData] = useState({});
  const [rawPortfoliosData, setRawPortfoliosData] = useState({});
  const [platformsData, setPlatformsData] = useState({
    classicChangeApps: [],
    rapidChangeApps: [],
    classicRapidChangeApps: [],
    overviewCards: {
      allApplications: {
        notOnboardedApps: [],
        uniqueOnboardedAppIds: []
      },
      visibilityHealth: {
        changesVisible: [],
        changesNotVisible: []
      },
      deploymentChannels: {
        greenChannelApps: [],
        redChannelApps: []
      }
    }
  });
  const [portfoliosData, setPortfoliosData] = useState({
    classicChangeApps: [],
    rapidChangeApps: [],
    classicRapidChangeApps: [],
    overviewCards: {
      allApplications: {
        notOnboardedApps: [],
        uniqueOnboardedAppIds: []
      },
      visibilityHealth: {
        changesVisible: [],
        changesNotVisible: []
      },
      deploymentChannels: {
        greenChannelApps: [],
        redChannelApps: []
      }
    }
  });
  
  // Selection states for platforms and portfolios
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedPortfolio, setSelectedPortfolio] = useState("");
  const [platformDropdownOpen, setPlatformDropdownOpen] = useState(false);
  const [portfolioDropdownOpen, setPortfolioDropdownOpen] = useState(false);
  const platformDropdownRef = useRef(null);
  const portfolioDropdownRef = useRef(null);
  
  const [isDataLoading, setIsDataLoading] = useState(true);
  
  // Onboarding wizard state
  const [isOnboardingWizardOpen, setIsOnboardingWizardOpen] = useState(false);
  
  // Onboarding requests state
  const [userOnboardingRequests, setUserOnboardingRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [userType, setUserType] = useState("");
  
  const navigate = useNavigate();
  const { token, graphData, tokenFetched,setTokenFetched } = useContext(TableContext);

  // Function to get CMDB token from backend
  const getToken = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_CMDB_BACKEND_URI}/getToken`, 
        { userEmail: graphData?.mail },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.status === 200) {
        const data = response.data;
        localStorage.setItem("cmdb_token", data.data);
        setIsCMDBTokenFetched(true);
        return data;
      } else {
        console.error("Failed to fetch CMDB token - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching CMDB token:", error);
      throw error;
    }
  };

  // Function to get AD token from backend
  

  // Function to fetch user onboarding requests
  const fetchUserOnboardingRequests = async () => {
    try {
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/my_onboarding_requests`,
        {
          user_name: graphData.displayName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.data && result.data.onboarding_requests) {
          setUserOnboardingRequests(result.data.onboarding_requests);
        } else {
          setUserOnboardingRequests([]);
        }
      } else {
        console.error("Failed to fetch onboarding requests - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user onboarding requests:", error);
      toast.error("Failed to load onboarding requests data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setUserOnboardingRequests([]);
    }
  };

  // Function to fetch pending approvals
  const fetchPendingApprovals = async () => {
    try {
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/my_onboarding_approvals`,
        {
          user_name: graphData.displayName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.data && result.data.pending_approvals) {
          // Store the user type
          const userTypeFromResponse = result.data.user_type || "";
          setUserType(userTypeFromResponse);
          
          // Filter approvals based on user type and approval type logic
          let filteredApprovals = result.data.pending_approvals;
          
          if (userTypeFromResponse === "application_owner") {
            // Show only approvals with approval_type "engineering_team"
            filteredApprovals = result.data.pending_approvals.filter(
              approval => approval.approval_type === "engineering_team"
            );
          } else if (userTypeFromResponse === "change_manager") {
            // Show only approvals with approval_type "change_manager"
            filteredApprovals = result.data.pending_approvals.filter(
              approval => approval.approval_type === "change_manager"
            );
          }
          
          setPendingApprovals(filteredApprovals);
        } else {
          setPendingApprovals([]);
          setUserType("");
        }
      } else {
        console.error("Failed to fetch pending approvals - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
      toast.error("Failed to load pending approvals data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setPendingApprovals([]);
      setUserType("");
    }
  };

  // Function to fetch user applications
  const fetchUserApplications = async () => {
    try {
      setIsDataLoading(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_user_applications`,
        {
          // user_email: graphData.mail,
          user_email: graphData.mail.replace('@maersk.com', '@maersktest.com'),
          user_name: graphData.displayName,
          // user_email: "bipeen.kulkarni@maersktest.com",
          // user_name: "Bipeen Kulkarni"
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Transform the data using the updated function
        const transformedData = mapApiDataToComponentFormat(result);
        setApplicationsData({
          classicChangeApps: transformedData.classicChangeApps,
          rapidChangeApps: transformedData.rapidChangeApps,
          classicRapidChangeApps: transformedData.classicRapidChangeApps,
          overviewCards: transformedData.overviewCards
        });
      } else {
        console.error("Failed to fetch applications - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user applications:", error);
      toast.error("Failed to load applications data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setApplicationsData({
        classicChangeApps: [],
        rapidChangeApps: [],
        classicRapidChangeApps: [],
        overviewCards: {
          allApplications: {
            notOnboardedApps: [],
            uniqueOnboardedAppIds: []
          },
          visibilityHealth: {
            changesVisible: [],
            changesNotVisible: []
          },
          deploymentChannels: {
            greenChannelApps: [],
            redChannelApps: []
          }
        }
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  // Function to fetch user platforms
  const fetchUserPlatforms = async () => {
    try {
      setIsDataLoading(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_user_platforms`,
        {
          // user_email: graphData.mail,
          user_email: graphData.mail.replace('@maersk.com', '@maersktest.com'),
          user_name: graphData.displayName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Store the raw platforms data
        if (result.data) {
          setRawPlatformsData(result.data);
          
          // Get available platform names
          const platformNames = Object.keys(result.data);
          
          // Set the first platform as selected if none is selected
          if (platformNames.length > 0 && !selectedPlatform) {
            const firstPlatform = platformNames[0];
            setSelectedPlatform(firstPlatform);
            
            // Transform data for the first platform
            const platformData = result.data[firstPlatform];
            const mockPlatformResponse = { data: platformData };
            const transformedData = mapApiDataToComponentFormat(mockPlatformResponse);
            setPlatformsData(transformedData);
          }
        } else {
          setRawPlatformsData({});
          setPlatformsData({
            classicChangeApps: [],
            rapidChangeApps: [],
            classicRapidChangeApps: [],
            overviewCards: {
              allApplications: {
                notOnboardedApps: [],
                uniqueOnboardedAppIds: []
              },
              visibilityHealth: {
                changesVisible: [],
                changesNotVisible: []
              },
              deploymentChannels: {
                greenChannelApps: [],
                redChannelApps: []
              }
            }
          });
        }
      } else {
        console.error("Failed to fetch platforms - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user platforms:", error);
      toast.error("Failed to load platforms data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setRawPlatformsData({});
      setPlatformsData({
        classicChangeApps: [],
        rapidChangeApps: [],
        classicRapidChangeApps: [],
        overviewCards: {
          allApplications: {
            notOnboardedApps: [],
            uniqueOnboardedAppIds: []
          },
          visibilityHealth: {
            changesVisible: [],
            changesNotVisible: []
          },
          deploymentChannels: {
            greenChannelApps: [],
            redChannelApps: []
          }
        }
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  // Function to fetch data for a selected platform
  const fetchSelectedPlatformData = async (platformName) => {
    try {
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_selected_platform_data`,
        {
          user_name: graphData.displayName,
          platform_name: platformName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Check if the result has data for the selected platform
        if (result.data && result.data[platformName]) {
          const selectedPlatformData = result.data[platformName];
          const mockSelectedPlatformResponse = { data: selectedPlatformData };
          // Transform the data using the updated function
          const transformedData = mapApiDataToComponentFormat(mockSelectedPlatformResponse);
          setPlatformsData(transformedData);
        } else {
          // If no data for this platform, use the full result data structure
          const transformedData = mapApiDataToComponentFormat(result);
          setPlatformsData(transformedData);
        }
      } else {
        console.error("Failed to fetch platform data - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching platform data:", error);
      toast.error("Failed to load platform data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setPlatformsData({
        classicChangeApps: [],
        rapidChangeApps: [],
        classicRapidChangeApps: [],
        overviewCards: {
          allApplications: {
            notOnboardedApps: [],
            uniqueOnboardedAppIds: []
          },
          visibilityHealth: {
            changesVisible: [],
            changesNotVisible: []
          },
          deploymentChannels: {
            greenChannelApps: [],
            redChannelApps: []
          }
        }
      });
    }
  };

  // Function to fetch data for a selected portfolio
  const fetchSelectedPortfolioData = async (portfolioName) => {
    try {
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_selected_portfolio_data`,
        {
          user_name: graphData.displayName,
          portfolio_name: portfolioName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Check if the result has data for the selected portfolio
        if (result.data && result.data[portfolioName]) {
          const selectedPortfolioData = result.data[portfolioName];
          const mockSelectedPortfolioResponse = { data: selectedPortfolioData };
          // Transform the data using the updated function
          const transformedData = mapApiDataToComponentFormat(mockSelectedPortfolioResponse);
          setPortfoliosData(transformedData);
        } else {
          // If no data for this portfolio, use the full result data structure
          const transformedData = mapApiDataToComponentFormat(result);
          setPortfoliosData(transformedData);
        }
      } else {
        console.error("Failed to fetch portfolio data - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
      toast.error("Failed to load portfolio data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setPortfoliosData({
        classicChangeApps: [],
        rapidChangeApps: [],
        classicRapidChangeApps: [],
        overviewCards: {
          allApplications: {
            notOnboardedApps: [],
            uniqueOnboardedAppIds: []
          },
          visibilityHealth: {
            changesVisible: [],
            changesNotVisible: []
          },
          deploymentChannels: {
            greenChannelApps: [],
            redChannelApps: []
          }
        }
      });
    }
  };

  // Function to fetch user portfolios
  const fetchUserPortfolios = async () => {
    try {
      setIsDataLoading(true);
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/get_user_portfolios`,
        {
          // user_email: graphData.mail,
          user_email: graphData.mail.replace('@maersk.com', '@maersktest.com'),
          user_name: graphData.displayName
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        // Store the raw portfolios data
        if (result.data) {
          setRawPortfoliosData(result.data);
          
          // Get available portfolio names
          const portfolioNames = Object.keys(result.data);
          
          // Set the first portfolio as selected if none is selected
          if (portfolioNames.length > 0 && !selectedPortfolio) {
            const firstPortfolio = portfolioNames[0];
            setSelectedPortfolio(firstPortfolio);
            
            // Transform data for the first portfolio
            const portfolioData = result.data[firstPortfolio];
            const mockPortfolioResponse = { data: portfolioData };
            const transformedData = mapApiDataToComponentFormat(mockPortfolioResponse);
            setPortfoliosData(transformedData);
          }
        } else {
          setRawPortfoliosData({});
          setPortfoliosData({
            classicChangeApps: [],
            rapidChangeApps: [],
            classicRapidChangeApps: [],
            overviewCards: {
              allApplications: {
                notOnboardedApps: [],
                uniqueOnboardedAppIds: []
              },
              visibilityHealth: {
                changesVisible: [],
                changesNotVisible: []
              },
              deploymentChannels: {
                greenChannelApps: [],
                redChannelApps: []
              }
            }
          });
        }
      } else {
        console.error("Failed to fetch portfolios - HTTP status:", response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching user portfolios:", error);
      toast.error("Failed to load portfolios data", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
      setRawPortfoliosData({});
      setPortfoliosData({
        classicChangeApps: [],
        rapidChangeApps: [],
        classicRapidChangeApps: [],
        overviewCards: {
          allApplications: {
            notOnboardedApps: [],
            uniqueOnboardedAppIds: []
          },
          visibilityHealth: {
            changesVisible: [],
            changesNotVisible: []
          },
          deploymentChannels: {
            greenChannelApps: [],
            redChannelApps: []
          }
        }
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  // Fetch both CMDB and AD tokens on component mount
  useEffect(() => {
    if (tokenFetched && graphData?.mail) {
      setIsOnboardingTokenFetched(true);
      const fetchCMDBToken = async () => {
        try {     
          // Fetch CMDB token
          const cmdbResult = await Promise.all([
            getToken()
          ]);
          setIsCMDBTokenFetched(true);
        } catch (error) {
          console.error('Failed to fetch tokens:', error);
          toast.error("Failed to authenticate user. Please try again later.", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light"
          });
        }
      };

      fetchCMDBToken();
    }
  }, [tokenFetched, graphData?.mail]);
useEffect(() => {
    setGlobalContextValues(token, graphData, setTokenFetched,setIsCMDBTokenFetched,setIsOnboardingTokenFetched);
  }, [token, graphData, setTokenFetched]);
  // Fetch applications data after onboarding token is obtained
  useEffect(() => {
    if (isOnboardingTokenFetched) {
      const fetchAllData = async () => {
        setRequestsLoading(true);
        try {
          // Fetch all data simultaneously
          await Promise.all([
            fetchUserApplications(),
            fetchUserOnboardingRequests(),
            fetchPendingApprovals()
          ]);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setRequestsLoading(false);
        }
      };
      
      fetchAllData();
    }
  }, [isOnboardingTokenFetched]);

  // Generic filter function to apply search and filters to applications
  const filterApplications = (applications, searchValue, statusValue, processTypeValue, mvcFilter = false, bcaFilter = false) => {
    if (!applications || applications.length === 0) return applications;
    
    return applications.filter((app) => {
      // Search filter with null checks
      const matchesSearch = searchValue === "" || 
        (app.name && app.name.toLowerCase().includes(searchValue.toLowerCase())) ||
        (app.owner && app.owner.toLowerCase().includes(searchValue.toLowerCase())) ||
        (app.code && app.code.toLowerCase().includes(searchValue.toLowerCase()));
      
      // Status filter
      const matchesStatus = statusValue === "All Statuses" || 
        app.onboardingStatus === statusValue;
      
      // Process type filter
      const matchesProcessType = processTypeValue === "All Processes" || 
        app.processType === processTypeValue;
      
      // MVC filter - only show MVC applications when toggle is on
      const matchesMvc = !mvcFilter || (app.isMvc === true);
      
      // BCA filter - only show BCA applications when toggle is on
      const matchesBca = !bcaFilter || (app.isBca === true);
      
      return matchesSearch && matchesStatus && matchesProcessType && matchesMvc && matchesBca;
    });
  };

  // New function to get all applications combined from the data structure
  const getAllApplicationsFromData = (data) => {
    const onboardedApps = [
      ...(data.classicChangeApps || []),
      ...(data.rapidChangeApps || []),
      ...(data.classicRapidChangeApps || [])
    ];

    // Get not onboarded apps
    const notOnboardedApps = data?.overviewCards?.allApplications?.notOnboardedApps || [];
    
    // Transform not onboarded apps to match the structure
    const transformedNotOnboardedApps = notOnboardedApps.map(app => ({
      id: `not-onboarded-${app.code || app.name}`,
      name: app.name,
      code: app.code,
      owner: Array.isArray(app.owners) ? app.owners[0] : (app.owners || ''),
      onboardingStatus: 'Not Onboarded',
      processType: '',
      lastChange: '',
      changesLast3Months: 0,
      channelStatus: '',
      deploymentChannel: '',
      visibilityHealth: false,
      changeProcessType: 'not_onboarded',
      mvc: app.mvc || 'No',
      bca: app.bca || 'No',
      isMvc: app.isMvc || false,
      isBca: app.isBca || false
    }));

    return [...onboardedApps, ...transformedNotOnboardedApps];
  };

  // Filter function for combined applications
  const filterCombinedApplications = (data, searchValue, statusValue, processTypeValue, mvcFilter = false, bcaFilter = false) => {
    const allApps = getAllApplicationsFromData(data);
    return filterApplications(allApps, searchValue, statusValue, processTypeValue, mvcFilter, bcaFilter);
  };

  // Get filtered data based on selected tab
  const getFilteredData = () => {
    switch (selectedTab) {
      case 0: // Applications
        const filteredApps = filterCombinedApplications(applicationsData, searchTerm, statusFilter, processTypeFilter, isMvcOn, isBcaOn);
        // Separate the filtered apps back into their categories for the data structure
        const filteredClassicApps = filteredApps.filter(app => app.changeProcessType === 'classic');
        const filteredRapidApps = filteredApps.filter(app => app.changeProcessType === 'rapid');
        const filteredClassicRapidApps = filteredApps.filter(app => app.changeProcessType === 'classic+rapid');
        
        return {
          ...applicationsData,
          classicChangeApps: filteredClassicApps,
          rapidChangeApps: filteredRapidApps,
          classicRapidChangeApps: filteredClassicRapidApps,
          filteredCombinedApps: filteredApps // Add the combined filtered apps for ApplicationTable
        };
      case 1: // Platforms
        const filteredPlatformApps = filterCombinedApplications(platformsData, platformSearchTerm, platformStatusFilter, platformProcessTypeFilter, platformIsMvcOn, platformIsBcaOn);
        const filteredPlatformClassicApps = filteredPlatformApps.filter(app => app.changeProcessType === 'classic');
        const filteredPlatformRapidApps = filteredPlatformApps.filter(app => app.changeProcessType === 'rapid');
        const filteredPlatformClassicRapidApps = filteredPlatformApps.filter(app => app.changeProcessType === 'classic+rapid');
        
        return {
          ...platformsData,
          classicChangeApps: filteredPlatformClassicApps,
          rapidChangeApps: filteredPlatformRapidApps,
          classicRapidChangeApps: filteredPlatformClassicRapidApps,
          filteredCombinedApps: filteredPlatformApps
        };
      case 2: // Portfolios
        const filteredPortfolioApps = filterCombinedApplications(portfoliosData, portfolioSearchTerm, portfolioStatusFilter, portfolioProcessTypeFilter, portfolioIsMvcOn, portfolioIsBcaOn);
        const filteredPortfolioClassicApps = filteredPortfolioApps.filter(app => app.changeProcessType === 'classic');
        const filteredPortfolioRapidApps = filteredPortfolioApps.filter(app => app.changeProcessType === 'rapid');
        const filteredPortfolioClassicRapidApps = filteredPortfolioApps.filter(app => app.changeProcessType === 'classic+rapid');
        
        return {
          ...portfoliosData,
          classicChangeApps: filteredPortfolioClassicApps,
          rapidChangeApps: filteredPortfolioRapidApps,
          classicRapidChangeApps: filteredPortfolioClassicRapidApps,
          filteredCombinedApps: filteredPortfolioApps
        };
      default:
        return applicationsData;
    }
  };

  // Get filtered applications data (for backward compatibility)
  const getFilteredApplicationsData = () => {
    return getFilteredData();
  };

  // Clear filters based on selected tab
  const clearFilters = () => {
    switch (selectedTab) {
      case 0: // Applications
        setSearchTerm("");
        setStatusFilter("All Statuses");
        setProcessTypeFilter("All Processes");
        setIsMvcOn(false);
        setIsBcaOn(false);
        break;
      case 1: // Platforms
        setPlatformSearchTerm("");
        setPlatformStatusFilter("All Statuses");
        setPlatformProcessTypeFilter("All Processes");
        setPlatformIsMvcOn(false);
        setPlatformIsBcaOn(false);
        break;
      case 2: // Portfolios
        setPortfolioSearchTerm("");
        setPortfolioStatusFilter("All Statuses");
        setPortfolioProcessTypeFilter("All Processes");
        setPortfolioIsMvcOn(false);
        setPortfolioIsBcaOn(false);
        break;
    }
  };

  // Get current filter values based on selected tab
  const getCurrentFilters = () => {
    switch (selectedTab) {
      case 0:
        return { searchTerm, statusFilter, processTypeFilter, isMvcOn, isBcaOn };
      case 1:
        return { 
          searchTerm: platformSearchTerm, 
          statusFilter: platformStatusFilter, 
          processTypeFilter: platformProcessTypeFilter,
          isMvcOn: platformIsMvcOn,
          isBcaOn: platformIsBcaOn
        };
      case 2:
        return { 
          searchTerm: portfolioSearchTerm, 
          statusFilter: portfolioStatusFilter, 
          processTypeFilter: portfolioProcessTypeFilter,
          isMvcOn: portfolioIsMvcOn,
          isBcaOn: portfolioIsBcaOn
        };
      default:
        return { searchTerm, statusFilter, processTypeFilter, isMvcOn, isBcaOn };
    }
  };

  // Handle filter changes based on selected tab
  const handleFilterChange = (filterType, value) => {
    switch (selectedTab) {
      case 0: // Applications
        if (filterType === 'search') setSearchTerm(value);
        else if (filterType === 'status') setStatusFilter(value);
        else if (filterType === 'process') setProcessTypeFilter(value);
        else if (filterType === 'mvc') setIsMvcOn(value);
        else if (filterType === 'bca') setIsBcaOn(value);
        break;
      case 1: // Platforms
        if (filterType === 'search') setPlatformSearchTerm(value);
        else if (filterType === 'status') setPlatformStatusFilter(value);
        else if (filterType === 'process') setPlatformProcessTypeFilter(value);
        else if (filterType === 'mvc') setPlatformIsMvcOn(value);
        else if (filterType === 'bca') setPlatformIsBcaOn(value);
        break;
      case 2: // Portfolios
        if (filterType === 'search') setPortfolioSearchTerm(value);
        else if (filterType === 'status') setPortfolioStatusFilter(value);
        else if (filterType === 'process') setPortfolioProcessTypeFilter(value);
        else if (filterType === 'mvc') setPortfolioIsMvcOn(value);
        else if (filterType === 'bca') setPortfolioIsBcaOn(value);
        break;
    }
  };

  // Handle platform selection
  const handlePlatformChange = async (platform) => {
    setSelectedPlatform(platform);
    setPlatformDropdownOpen(false);
    
    setIsDataLoading(true);
    try {
      // Check if platform data is already available in rawPlatformsData
      if (rawPlatformsData[platform]) {
        // Use existing data from the initial fetch
        const platformData = rawPlatformsData[platform];
        const mockPlatformResponse = { data: platformData };
        const transformedData = mapApiDataToComponentFormat(mockPlatformResponse);
        setPlatformsData(transformedData);
      } else {
        // Fetch data for the selected platform using the new API
        await fetchSelectedPlatformData(platform);
      }
    } catch (error) {
      console.error("Error loading platform data:", error);
    } finally {
      setIsDataLoading(false);
    }
  };

  // Handle portfolio selection
  const handlePortfolioChange = async (portfolio) => {
    setSelectedPortfolio(portfolio);
    setPortfolioDropdownOpen(false);
    
    setIsDataLoading(true);
    try {
      // Check if portfolio data is already available in rawPortfoliosData
      if (rawPortfoliosData[portfolio]) {
        // Use existing data from the initial fetch
        const portfolioData = rawPortfoliosData[portfolio];
        const mockPortfolioResponse = { data: portfolioData };
        const transformedData = mapApiDataToComponentFormat(mockPortfolioResponse);
        setPortfoliosData(transformedData);
      } else {
        // Fetch data for the selected portfolio using the new API
        await fetchSelectedPortfolioData(portfolio);
      }
    } catch (error) {
      console.error("Error loading portfolio data:", error);
    } finally {
      setIsDataLoading(false);
    }
  };

  // Handle dropdown toggles
  const handlePlatformDropdownToggle = () => {
    setPlatformDropdownOpen(!platformDropdownOpen);
    setPortfolioDropdownOpen(false);
  };

  const handlePortfolioDropdownToggle = () => {
    setPortfolioDropdownOpen(!portfolioDropdownOpen);
    setPlatformDropdownOpen(false);
  };

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target)) {
        setPlatformDropdownOpen(false);
      }
      if (portfolioDropdownRef.current && !portfolioDropdownRef.current.contains(event.target)) {
        setPortfolioDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle tab change and fetch data if needed
  const handleTabChange = (newIndex) => {
    setSelectedTab(newIndex);
    
    // Fetch data for the selected tab if needed
    switch (newIndex) {
      case 1:
        // For platforms, fetch user platforms if not already loaded
        if (Object.keys(rawPlatformsData).length === 0) {
          fetchUserPlatforms();
        }
        break;
      case 2:
        if (Object.keys(rawPortfoliosData).length === 0) {
          fetchUserPortfolios();
        }
        break;
      default:
        // Applications data is already loaded on component mount
        break;
    }
  };

  // Function to handle onboarding wizard close and refresh requests
  const handleOnboardingWizardClose = async () => {
    setIsOnboardingWizardOpen(false);
    
    // Refresh onboarding requests data when returning to dashboard
    try {
      setRequestsLoading(true);
      await Promise.all([
        fetchUserOnboardingRequests(),
        fetchPendingApprovals()
      ]);
    } catch (error) {
      console.error("Error refreshing onboarding requests:", error);
    } finally {
      setRequestsLoading(false);
    }
  };

  return (
    <div className='home' style={{marginBottom: '20px'}}>
    <Box sx={{ padding: 3 }}>
      {/* Onboarding Wizard */}
      {isOnboardingWizardOpen ? (
        <OnboardingWizard 
          onClose={handleOnboardingWizardClose}
        />
      ) : (
        <>
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontFamily: maerskFontFamily }}>
                Change Onboarding Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontFamily: maerskFontFamily }}>
                Monitor and manage change onboarding status of applications across your ownership hierarchy
              </Typography>
            </Box>
            <McButton 
              variant="filled"
              icon="plus"
              onClick={() => {
                setIsOnboardingWizardOpen(true);
              }}
            >
              Onboard New Application
            </McButton>
          </Box>

          {/* Tabs using McTabBar */}
          <Box sx={{mb: 2}}>
            <McTabBar 
              tabchange={(e) => handleTabChange(e.detail)} 
              currentindex={selectedTab}
            >
              <McTab 
                slot="tab" 
                label="My Applications" 
                active={selectedTab === 0}
                style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
              />
              <div slot="panel"></div>
              <McTab 
                slot="tab" 
                label="My Platform Apps" 
                disabled={!tokenFetched}
                active={selectedTab === 1}
                style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
              />
              <div slot="panel"></div>
              <McTab 
                slot="tab" 
                label="My Portfolio Apps"
                disabled={!tokenFetched}
                active={selectedTab === 2}
                style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
              />
              <div slot="panel"></div>
            </McTabBar>
          </Box>

          {/* Platform/Portfolio Selection Dropdown */}
          {!isDataLoading && (selectedTab === 1 || selectedTab === 2) && (
            <Box sx={{ mb: 3, textAlign: "center" }}>
              {selectedTab === 1 && (
                <div className="content-lp-item" style={{ marginBottom: 20 }}>
                  <Box sx={{ textAlign: "center", position: "relative" }} ref={platformDropdownRef}>
                    {allAvailablePlatforms.length > 1 ? (
                      <>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "600",
                            color: "#333",
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: '1rem',
                            fontFamily: maerskFontFamily
                          }}
                          onClick={handlePlatformDropdownToggle}
                        >
                          {selectedPlatform || "Select a platform"}
                          {platformDropdownOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" sx={{ ml: 0.5 }} />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Typography>
                        {platformDropdownOpen && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              top: "100%",
                              mt: 1,
                              bgcolor: "white",
                              boxShadow: 3,
                              borderRadius: 1,
                              zIndex: 5000,
                              minWidth: 300,
                              maxHeight: 200,
                              overflowY: "auto",
                              p: 1,
                              fontFamily: maerskFontFamily
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {allAvailablePlatforms.map((platform) => (
                              <Typography
                                key={platform}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  color: platform === selectedPlatform ? "#aaa" : "#333",
                                  cursor: platform === selectedPlatform ? "not-allowed" : "pointer",
                                  "&:hover": platform === selectedPlatform ? {} : { bgcolor: "#f0f0f0" },
                                  opacity: platform === selectedPlatform ? 0.6 : 1,
                                  textAlign: "left",
                                  fontFamily: maerskFontFamily
                                }}
                                onClick={() => {
                                  if (platform !== selectedPlatform) {
                                    handlePlatformChange(platform);
                                    setPlatformDropdownOpen(false);
                                  }
                                }}
                              >
                                {platform}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          fontSize: '1rem',
                          fontFamily: maerskFontFamily
                        }}
                      >
                        {selectedPlatform || allAvailablePlatforms[0] || "No platform available"}
                      </Typography>
                    )}
                  </Box>
                </div>
              )}
              
              {selectedTab === 2 && (
                <div className="content-lp-item" style={{ marginBottom: 20 }}>
                  <Box sx={{ textAlign: "center", position: "relative" }} ref={portfolioDropdownRef}>
                    {allAvailablePortfolios.length > 1 ? (
                      <>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "600",
                            color: "#333",
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontSize: '1rem',
                            fontFamily: maerskFontFamily
                          }}
                          onClick={handlePortfolioDropdownToggle}
                        >
                          {selectedPortfolio || "Select a portfolio"}
                          {portfolioDropdownOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" sx={{ ml: 0.5 }} />
                          ) : (
                            <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
                          )}
                        </Typography>
                        {portfolioDropdownOpen && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              top: "100%",
                              mt: 1,
                              bgcolor: "white",
                              boxShadow: 3,
                              borderRadius: 1,
                              zIndex: 5000,
                              minWidth: 300,
                              maxHeight: 200,
                              overflowY: "auto",
                              p: 1,
                              fontFamily: maerskFontFamily
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {allAvailablePortfolios.map((portfolio) => (
                              <Typography
                                key={portfolio}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  color: portfolio === selectedPortfolio ? "#aaa" : "#333",
                                  cursor: portfolio === selectedPortfolio ? "not-allowed" : "pointer",
                                  "&:hover": portfolio === selectedPortfolio ? {} : { bgcolor: "#f0f0f0" },
                                  opacity: portfolio === selectedPortfolio ? 0.6 : 1,
                                  textAlign: "left",
                                  fontFamily: maerskFontFamily
                                }}
                                onClick={() => {
                                  if (portfolio !== selectedPortfolio) {
                                    handlePortfolioChange(portfolio);
                                    setPortfolioDropdownOpen(false);
                                  }
                                }}
                              >
                                {portfolio}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "600",
                          color: "#333",
                          fontSize: '1rem',
                          fontFamily: maerskFontFamily
                        }}
                      >
                        {selectedPortfolio || allAvailablePortfolios[0] || "No portfolio available"}
                      </Typography>
                    )}
                  </Box>
                </div>
              )}
            </Box>
          )}

          {/* Content Section */}
          {isDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Overview Cards */}
              <OverviewCards 
                selectedTab={selectedTab}
                applicationsData={getFilteredData()}
                filteredCombinedApps={getFilteredData().filteredCombinedApps}
              />

              {/* Filter Section - Available for all tabs */}
              <FilterSection
                searchTerm={getCurrentFilters().searchTerm}
                onSearchChange={(value) => handleFilterChange('search', value)}
                statusFilter={getCurrentFilters().statusFilter}
                onStatusFilterChange={(value) => handleFilterChange('status', value)}
                processTypeFilter={getCurrentFilters().processTypeFilter}
                onProcessTypeFilterChange={(value) => handleFilterChange('process', value)}
                isMvcOn={getCurrentFilters().isMvcOn}
                onMvcToggle={(e) => handleFilterChange('mvc', e.target.checked)}
                isBcaOn={getCurrentFilters().isBcaOn}
                onBcaToggle={(e) => handleFilterChange('bca', e.target.checked)}
                onClearFilters={clearFilters}
              />

              {/* Applications Table */}
              <ApplicationTable
                applications={getFilteredData()}
                classicChangeApps={getFilteredData().classicChangeApps}
                rapidChangeApps={getFilteredData().rapidChangeApps}
                classicRapidChangeApps={getFilteredData().classicRapidChangeApps}
                title={
                  selectedTab === 0 
                    ? "My Applications" 
                    : selectedTab === 1 
                    ? `Platform Applications: ${selectedPlatform}`
                    : `Portfolio Applications: ${selectedPortfolio}`
                }
                showProcessTabs={true} // Show process tabs for all views
              />

              {/* Onboarding Requests Table - Only show for My Applications tab */}
              {selectedTab === 0 && (
                <OnboardingRequestsTable
                  userRequests={userOnboardingRequests}
                  pendingApprovals={pendingApprovals}
                  title="My Onboarding Requests"
                  onPendingApprovalsRefresh={fetchPendingApprovals}
                  onUserRequestsRefresh={fetchUserOnboardingRequests}
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
    </div>
  );
};

export default ApplicationOnboarding;
